import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { datesService } from '../../services/DatesService';
import { ListingListItemImage } from '@showclix-shared/ListingComponents';
import { AppContext } from '../../App';
import { EVENT_SALE_PAST, EVENT_SALE_FUTURE, EVENT_SALE_ACTIVE } from '../../constants/onsaleStatus';
import Toast from "@showclix-shared/Toast"
import { useAnalytics, useBaseEventData } from '@/contexts/Analytics';
import _omit from 'lodash/omit';
import { FRBadge, FRText } from '@patrontech/frontrow-ui';

const HERO_LAYOUT = 'hero';

const ListingListItem = props => {
  const {
    t,
    embeddedCheckout,
    loadingListingId,
    setLoadingListingId,
    embedCaughtRenderingError,
    hideStartingPrices,
  } = useContext(AppContext);
  const {
    getListingTime,
    getFormattedEventStart,
  } = datesService();
  const { is_sold_out, has_presale } = props.listing;

  const isNotOnSale = listing => listing.is_sold_out || listing.onsale_status === EVENT_SALE_PAST || listing.onsale_status === EVENT_SALE_FUTURE || !listing.has_active_price_levels;
  const baseEventData = useBaseEventData();

  const { reportEvent, reportGTM } = useAnalytics();

  const getImage = listing => {
    if (listing.image && listing.image !== '') {
      const containerStyle = { backgroundImage: '' };
      if (listing?.layout === HERO_LAYOUT && !listing?.background_image) {
        containerStyle.backgroundImage = `url("${listing?.background_image}")`;
      }

      return (<ListingListItemImage
        src={listing.image}
        alt={listing.listing_title}
        style={containerStyle}
        className={listing?.layout === HERO_LAYOUT && listing.background_image ? 'listing-item__hero__container--has-bg-img' : ''}
      />);
    } else {
      return (
        <div className="listing-item__image listing-item__image--empty">
          <i className="icon-calendar" aria-hidden="true" />
        </div>
      );
    }
  };

  const getCheckoutLink = listing => '/tickets/' + listing.listing_slug;

  const getBuyTicketsLink = listing => getCheckoutLink(listing);

  const getDate = listing => {
    if (listing.custom_time) {
      return listing.custom_time;
    }

    if (parseInt(listing.children_count) > 0 && !listing.single_event_series) {
      return t('org_listing__multiple_dates_and_times');
    }

    if (listing.multipleTimes) {
      return 'Multiple Times';
    }

    return (<>
      <span>{getFormattedEventStart(listing.event_start)}</span>{' · '}
      <span>{getListingTime(listing.event_start)}{listing.event_end ? ` - ${getListingTime(listing.event_end)}` : ''}</span>
    </>);
  };

  const getVenueInfo = listing => {
    if (listing.custom_venue) {
      return listing.custom_venue;
    }
    return `${listing.venue_name}, ${listing.venue_city}`;
  };

  const getStartingPrice = listing => {
    if (isNotOnSale(listing)) {
      return null;
    }

    if(hideStartingPrices) {
      return null;
    }

    if (listing.has_only_free_tickets) {
      return (<FRText variant="body-xs">{t('org_listing__free_entry')}</FRText>);
    }

    if (!isNaN(listing.min_price) && listing.min_price_fmt) {
      return (<FRText variant="body-xs">{t('org_listing__start_at') + ' ' + listing.min_price_fmt}</FRText>);
    }

    return null;
  }

  const reportOpenListing = () => {
    reportEvent('google-analytics-4')('event', 'orglisting_event_selected', {
      ..._omit(baseEventData, ['event_ids', 'event_names']),
      series: props.listing.is_series,
      reserved: props.listing.is_seated,
    });
    reportGTM({
      event: 'orglisting_event_selected',
      ..._omit(baseEventData, ['event_ids', 'event_names']),
      series: props.listing.is_series,
      reserved: props.listing.is_seated,
    });
  }

  const loadDynamicListing = async (e) => {
    e.preventDefault();

    if (loadingListingId === props.listing.listing_id || embedCaughtRenderingError) {
      return;
    }

    setLoadingListingId(props.listing.listing_id);
    const loadListingResult = await embeddedCheckout.openCheckoutToListing(
      props.listing.listing_slug,
      props.listing.primary_event_id
    );
    if (!loadListingResult) {
      Toast.error(t('org_listing__an_error_occurred'));
    }
    setLoadingListingId(null);
    reportOpenListing();
  }

  const getDisplayAges = listing => {
    return `${listing?.display_ages} ${t('org_listing__event')}`;
  };

  const { Element, ...triggerProps } = useMemo(() => {
    if (embeddedCheckout) {
      return {
        Element: 'a',
        href: '#',
        onClick: loadDynamicListing,
      }
    }

    return {
      Element: 'a',
      href: getBuyTicketsLink(props.listing),
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: reportOpenListing,
    }
  }, [embeddedCheckout, loadingListingId, embedCaughtRenderingError, props.listing]);

  return (
    <Element {...triggerProps} className="listing-item" id={`listing-item__button-${props.listing.listing_id}`}>
      <div className="listing-item__hero">{getImage(props.listing)}</div>

      <div className="listing-item__content">
        {(is_sold_out || (props.listing.onsale_status === EVENT_SALE_FUTURE && has_presale)) && (
          <FRBadge className="listing-item__sale-status" size="lg" emphasis="strong" colorScheme={is_sold_out ? "warning" : "info"}>
            {is_sold_out ? t('org_listing__sold_out') : t('org_listing__presale')}
          </FRBadge>
        )}
        <header className="listing-item__header">
          {props.listing?.show_date_and_time && (
            <FRText className="listing-item__event-date" variant="body-xs">
              {getDate(props.listing)}
            </FRText>
          )}
          {props.decodeTitle && <FRText className="listing-item__listing-title" variant="body" element="h3" weight="bold" dangerouslySetInnerHTML={{ __html: props.listing.listing_title }} />}
          {!props.decodeTitle && <FRText className="listing-item__listing-title" variant="body" element="h3" weight="bold">{props.listing.listing_title}</FRText>}
          {props.listing?.show_venue && (props?.listing?.venue_name || props?.listing?.venue_city) && (
            <FRText className="listing-item__venue-info" variant="body-xs">
              {getVenueInfo(props.listing)}
            </FRText>
          )}
          {props?.listing?.display_ages && (
            <FRText className="listing-item__age-info" variant="body-xs">
              {getDisplayAges(props.listing)}
            </FRText>
          )}
        </header>

        <footer className="listing-item__footer listing-item__pricing-info">
          {getStartingPrice(props.listing)}
        </footer>
      </div>
    </Element>
  );
};

ListingListItem.propTypes = {
  listing: PropTypes.object,
  decodeTitle: PropTypes.bool
};

export const ListingListItemSkeleton = () => {
  return (
    <div className="listing-item">
      <div className="listing-item__hero">
        <div className="listing-item__image listing-item__image--empty">
          <i className="icon-calendar" aria-hidden="true" />
        </div>
      </div>

      <div className="listing-item__content listing-item__content--loading">
        <div className="listing-item__header">
          <p>&nbsp;</p>
          <h3>&nbsp;</h3>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
        </div>

        <div className="listing-item__footer">
          <p>&nbsp;</p>
        </div>
      </div>
    </div>
  );
};

export default ListingListItem;
